import React from "react";

export interface CurrentBalanceInsightItem {
  accentColor: "success" | "warning" | "neutral";
  description: string;
}

export interface CurrentBalanceInsightsPanelProps {
  monthLabel?: string;
  insights: CurrentBalanceInsightItem[];
  loading?: boolean;
}

const barClass: Record<
  CurrentBalanceInsightItem["accentColor"],
  string
> = {
  success: "bg-success-500",
  warning: "bg-error-500",
  neutral: "bg-neutral-400",
};

export function CurrentBalanceInsightsPanel({
  monthLabel = "January 2026 · Closed · Insights",
  insights,
  loading = false,
}: CurrentBalanceInsightsPanelProps) {
  if (loading) {
    return (
      <div className="flex flex-col items-start gap-4 flex-1 min-w-0">
        <div className="h-4 w-48 animate-pulse rounded bg-neutral-200" />
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex w-full gap-4 rounded-lg border border-solid border-neutral-100 bg-neutral-50 px-4 py-4 h-14 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!insights || insights.length === 0) {
    return (
      <div className="flex flex-col items-start gap-4 flex-1 min-w-0">
        <span className="text-caption-bold font-caption-bold text-neutral-400 uppercase tracking-widest">
          {monthLabel}
        </span>
        <div className="flex w-full flex-col gap-2 rounded-lg border border-solid border-neutral-100 bg-neutral-50 px-4 py-4">
          <span className="text-body-bold font-body-bold text-neutral-700">
            Insights will appear after your first month close.
          </span>
          <span className="text-caption font-caption text-neutral-500">
            Close a month to see movement, concentration, and runway based on
            closed books.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-4 flex-1 min-w-0">
      <span className="text-caption-bold font-caption-bold text-neutral-400 uppercase tracking-widest">
        {monthLabel}
      </span>
      {insights.map((item, i) => (
        <div
          key={i}
          className="flex w-full gap-4 rounded-lg border border-solid border-neutral-100 bg-neutral-50 px-4 py-4 items-stretch"
        >
          <div
            className={`flex w-1 flex-none flex-col items-start rounded-full ${barClass[item.accentColor]}`}
          />
          <span className="text-body font-body text-neutral-700 flex-1">
            {item.description}
          </span>
        </div>
      ))}
    </div>
  );
}
